import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, FlaskConical, ListFilter, Split, Trophy, Users } from 'lucide-react';
import { useCampaigns } from 'core/features/campaigns/hooks';
import { Page } from 'components/ui/page';
import PageHeader from 'components/ui/PageHeader';
import { Button } from 'components/ui/button';
import { SearchInput } from 'components/ui/search-input';
import { IconTile } from 'components/ui/icon-tile';
import { ErrorState } from 'components/ui/error-state';
import { humanizeStatus, tone, toneForStatus, type Tone } from 'components/ui/tone';
import { cn } from 'lib/utils';
import ABCampaignResults, { type ABResolution } from './ABCampaignResults';

/* ══════════════════════════════════════════════════════════════════════════
   A/B Testing — pick a campaign, read the verdict.

   The picker used to be a flat list of every campaign with nothing to tell an
   A/B campaign apart from a regular one, so the common path was: click, read
   "No A/B test for this campaign", click again. The campaigns endpoint does
   not report which campaigns are tests, and inventing that label would be a
   lie — but every campaign the user opens *does* answer the question, so the
   answer is now kept and shown on the row. The list gets more useful as it is
   used, and no dead end has to be walked twice.
   ══════════════════════════════════════════════════════════════════════════ */

const STEPS = [
    { Icon: Split, title: 'Two versions go out', text: 'Variant A and Variant B are sent to separate groups inside the test audience.' },
    { Icon: Trophy, title: 'One is picked', text: 'After the evaluation window, the campaign selects the version that performed better.' },
    { Icon: Users, title: 'The winner follows', text: 'That version is then sent to the audience held back from the test, and reported separately.' },
];

/** What we learned about a campaign by opening it — never guessed. */
function outcomeChip(outcome?: ABResolution): { label: string; tone: Tone } | null {
    if (!outcome) return null;
    if (!outcome.isABTest) return { label: 'No test', tone: 'neutral' };
    if (outcome.hasWinner) return { label: 'Winner', tone: 'success' };
    return { label: 'A/B', tone: 'violet' };
}

export default function ABTestingPage() {
    const [pageSize, setPageSize] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
    const [outcomes, setOutcomes] = useState<Record<string, ABResolution>>({});

    const { data, isPending, isFetching, isError, refetch } = useCampaigns({ page });

    useEffect(() => {
        if (data?.page === 1 && data.items.length) setPageSize(data.items.length);
    }, [data]);

    const campaigns = useMemo(
        () => data?.items.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase())) ?? [],
        [data, search]
    );
    const changePage = (next: number) => {
        setSearch('');
        setPage(next);
    };

    const totalPages = pageSize > 0 && data?.total ? Math.ceil(data.total / pageSize) : 0;
    const hasPager = page > 1 || (data?.total ?? 0) > pageSize;

    const handleResolved = useCallback((id: string, resolution: ABResolution) => {
        setOutcomes((prev) => {
            const known = prev[id];
            if (known && known.isABTest === resolution.isABTest && known.hasWinner === resolution.hasWinner) return prev;
            return { ...prev, [id]: resolution };
        });
    }, []);

    return (
        <Page>
            <PageHeader
                icon={<FlaskConical className="w-5 h-5" />}
                tone="violet"
                title="A/B Testing"
                subtitle="Compare the two versions of a campaign side by side, and follow the winning email through to its final send."
            />

            <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[17rem_minmax(0,1fr)] xl:grid-cols-[20rem_minmax(0,1fr)] xl:gap-6">
                {/* ── Campaign picker ─────────────────────────────────── */}
                <aside
                    aria-label="Campaign selection"
                    className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card lg:sticky lg:top-6"
                >
                    <div className="space-y-3 border-b border-line p-4">
                        <div className="flex items-center justify-between gap-2">
                            <h2 className="inline-flex items-center gap-2 text-title-sm font-semibold text-content">
                                <ListFilter className="w-4 h-4 text-content-muted" aria-hidden="true" />
                                Campaigns
                            </h2>
                            {!isPending && !isError && (
                                <span className="rounded-full border border-line bg-surface-sunken px-2 py-0.5 text-micro font-semibold tabular-nums text-content-secondary">
                                    {campaigns.length}
                                </span>
                            )}
                        </div>
                        <SearchInput
                            size="sm"
                            value={search}
                            onChange={setSearch}
                            placeholder="Filter this page…"
                            aria-label="Search campaigns on this page"
                        />
                    </div>

                    {isPending ? (
                        <div className="space-y-2 p-3" role="status" aria-label="Loading campaigns">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <div key={i} className="flex items-center gap-3 rounded-xl p-2.5">
                                    <span className="h-9 w-9 shrink-0 animate-pulse rounded-lg bg-surface-sunken" />
                                    <span className="h-3.5 flex-1 animate-pulse rounded bg-surface-sunken" />
                                </div>
                            ))}
                        </div>
                    ) : isError ? (
                        <div className="p-4">
                            <ErrorState
                                bare
                                className="px-0 py-6"
                                title="Couldn’t load campaigns"
                                description="The campaign list didn’t come back."
                                onRetry={() => refetch()}
                                retryLabel="Retry campaigns"
                            />
                        </div>
                    ) : (
                        <>
                            <div
                                className="scrollbar-slim max-h-[20rem] space-y-0.5 overflow-y-auto p-2 lg:max-h-[calc(100vh-20rem)]"
                                aria-busy={isFetching}
                            >
                                {campaigns.map((campaign) => {
                                    const id = String(campaign.id);
                                    const active = selected?.id === id;
                                    const chip = outcomeChip(outcomes[id]);
                                    const statusTone = tone(toneForStatus(campaign.c_status as string));
                                    return (
                                        <button
                                            key={campaign.id}
                                            type="button"
                                            aria-pressed={active}
                                            disabled={isFetching}
                                            onClick={() => setSelected({ id, name: campaign.name })}
                                            className={cn(
                                                'group relative flex w-full items-center gap-3 rounded-xl py-2.5 pl-4 pr-2 text-left transition-colors focus-ring disabled:opacity-50',
                                                active ? 'bg-violet-subtle' : 'hover:bg-surface-hover'
                                            )}
                                        >
                                            <span
                                                aria-hidden="true"
                                                className={cn(
                                                    'absolute left-0 top-1/2 w-1 -translate-y-1/2 rounded-r-full transition-all duration-200',
                                                    active ? 'h-8 bg-violet-solid' : 'h-0'
                                                )}
                                            />
                                            <span className="min-w-0 flex-1">
                                                <span className={cn('block truncate text-ui-lg font-medium', active ? 'text-violet-on-subtle' : 'text-content')}>
                                                    {campaign.name}
                                                </span>
                                                {(campaign.c_status || chip) && (
                                                    <span className="mt-1 flex items-center gap-2">
                                                        {campaign.c_status && (
                                                            <span className="inline-flex min-w-0 items-center gap-1.5 text-meta text-content-muted">
                                                                <span aria-hidden="true" className={cn('h-1.5 w-1.5 shrink-0 rounded-full', statusTone.accent)} />
                                                                <span className="truncate">{humanizeStatus(campaign.c_status as string)}</span>
                                                            </span>
                                                        )}
                                                        {chip && (
                                                            <span className={cn('shrink-0 rounded-full px-1.5 py-0.5 text-micro font-semibold', tone(chip.tone).badge)}>
                                                                {chip.label}
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </span>
                                            <ChevronRight
                                                aria-hidden="true"
                                                className={cn(
                                                    'w-4 h-4 shrink-0 transition-colors',
                                                    active ? 'text-violet' : 'text-content-subtle opacity-0 group-hover:opacity-100'
                                                )}
                                            />
                                        </button>
                                    );
                                })}

                                {!campaigns.length && (
                                    <p className="p-4 text-ui-lg text-content-muted">
                                        {search
                                            ? 'No matches on this page. Try another name, or browse the next page.'
                                            : 'No campaigns found.'}
                                    </p>
                                )}
                            </div>

                            {hasPager && (
                                <div className="flex items-center justify-between gap-2 border-t border-line p-2.5">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label="Previous campaigns"
                                        title="Previous campaigns"
                                        disabled={page === 1 || isFetching}
                                        onClick={() => changePage(page - 1)}
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </Button>
                                    <span className="text-meta tabular-nums text-content-muted">
                                        Page {page}
                                        {totalPages ? ` of ${totalPages}` : ''}
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label="Next campaigns"
                                        title="Next campaigns"
                                        disabled={!data?.items.length || isFetching || !pageSize || page * pageSize >= data.total}
                                        onClick={() => changePage(page + 1)}
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </aside>

                {/* ── Results ─────────────────────────────────────────── */}
                <section aria-label="Campaign results" className="min-w-0">
                    {selected ? (
                        <div className="space-y-5">
                            <h2 className="min-w-0 break-words text-title-lg font-bold tracking-tight text-content">
                                {selected.name}
                            </h2>
                            <ABCampaignResults
                                key={selected.id}
                                campaignId={selected.id}
                                onResolved={(resolution) => handleResolved(selected.id, resolution)}
                            />
                        </div>
                    ) : (
                        <div className="relative overflow-hidden rounded-2xl border border-line bg-surface px-6 py-12 text-center shadow-card sm:px-10 sm:py-16">
                            <span
                                aria-hidden="true"
                                className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-white/50 blur-3xl dark:bg-white/5"
                            />
                            <div className="relative">
                                <IconTile tone="violet" size="xl" className="mx-auto mb-5">
                                    <FlaskConical strokeWidth={1.75} />
                                </IconTile>
                                <h2 className="text-title-lg font-bold tracking-tight text-content">Which email works better?</h2>
                                <p className="mx-auto mt-2 max-w-md text-ui-lg leading-relaxed text-content-muted">
                                    Choose a campaign on the left to see its two versions compared metric by metric, and what
                                    happened after a winner was picked.
                                </p>

                                <ol className="mx-auto mt-12 grid max-w-3xl gap-8 text-left sm:grid-cols-3 sm:gap-6">
                                    {STEPS.map(({ Icon, title, text }, i) => (
                                        <li key={title} className="relative">
                                            <div className="flex items-center gap-2.5">
                                                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-subtle text-meta font-bold tabular-nums text-violet-on-subtle">
                                                    {i + 1}
                                                </span>
                                                <Icon className="w-4 h-4 text-content-subtle" aria-hidden="true" />
                                            </div>
                                            <h3 className="mt-3 text-ui-lg font-semibold text-content">{title}</h3>
                                            <p className="mt-1.5 text-ui-lg leading-relaxed text-content-muted">{text}</p>
                                            {i < STEPS.length - 1 && (
                                                <ChevronRight
                                                    aria-hidden="true"
                                                    className="absolute -right-4 top-1.5 hidden w-4 h-4 text-content-subtle sm:block"
                                                />
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </Page>
    );
}
